import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import type { AuditStatus } from "@/packages/types";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import {
  ActionButton,
  DevSection,
  EntityTable,
  JsonViewer,
  ResultPanel,
  StatusBadge,
} from "./-_dev-components";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ResultState = {
  data: unknown;
  error: unknown;
  timestamp: number;
  label: string;
};

const AUDIT_STATUSES = [
  "pending",
  "draft",
  "in_progress",
  "submitted",
  "approved",
  "rejected",
  "archived",
] as const;

const TRANSITION_TABLE: { from: string; allowed: string[] }[] = [
  { from: "pending", allowed: ["draft"] },
  { from: "draft", allowed: ["in_progress"] },
  { from: "in_progress", allowed: ["submitted"] },
  { from: "submitted", allowed: ["approved", "rejected"] },
  { from: "approved", allowed: ["archived"] },
  { from: "rejected", allowed: ["in_progress"] },
  { from: "archived", allowed: ["—"] },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function AuditStatusBadge({ status }: { status: string }) {
  const color =
    status === "pending"
      ? "bg-slate-500/20 text-slate-400 border-slate-500/30"
      : status === "draft"
        ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
        : status === "in_progress"
          ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
          : status === "submitted"
            ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
            : status === "approved"
              ? "bg-green-500/20 text-green-400 border-green-500/30"
              : status === "rejected"
                ? "bg-red-500/20 text-red-400 border-red-500/30"
                : "bg-gray-500/20 text-gray-400 border-gray-500/30";

  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function AuditsPlaygroundPage() {
  const audits = useQuery(api.audit_queries.listAudits, {});

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);

  const selectedAudit = audits?.find((a) => a.id === selectedId) ?? null;

  function handleSelect(id: string) {
    setSelectedId(id);
    setResult(null);
  }

  function makeResultHandler(label: string) {
    return (r: { data: unknown; error: unknown; timestamp: number }) =>
      setResult({ ...r, label });
  }

  return (
    <div className="flex h-full overflow-hidden font-mono text-sm">
      {/* Left panel — audit list */}
      <div className="flex w-80 shrink-0 flex-col border-r border-border p-3">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Audits ({audits?.length ?? "…"})
        </h2>
        <EntityTable
          rows={audits ?? []}
          isLoading={audits === undefined}
          selectedId={selectedId}
          onSelect={handleSelect}
          getId={(a) => a.id}
          columns={[
            {
              header: "ID",
              render: (a) => (
                <span className="font-mono text-xs text-muted-foreground">
                  …{a.id.slice(-8)}
                </span>
              ),
            },
            {
              header: "Title",
              render: (a) => (
                <span title={a.displayName}>
                  {a.displayName.length > 22
                    ? `${a.displayName.slice(0, 22)}…`
                    : a.displayName}
                </span>
              ),
            },
            {
              header: "Status",
              render: (a) => <AuditStatusBadge status={a.status} />,
            },
          ]}
        />
      </div>

      {!selectedAudit ? (
        <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
          Select an audit to inspect and run actions
        </div>
      ) : (
        <>
          {/* Middle panel — actions */}
          <div className="flex w-[520px] shrink-0 flex-col border-r border-border overflow-hidden">
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Header */}
              <div className="space-y-0.5">
                <h2 className="font-semibold truncate">
                  {selectedAudit.displayName}
                </h2>
                <span className="text-xs text-muted-foreground font-mono">
                  {selectedAudit.id}
                </span>
              </div>

              <JsonViewer data={selectedAudit} label="Raw Audit" />

              <StatusOverrideSection
                selectedId={selectedId!}
                currentStatus={selectedAudit.status}
                onResult={makeResultHandler}
              />

              <AIActionsSection
                selectedId={selectedId!}
                aiSummaryData={selectedAudit.aiSummaryData}
                onResult={makeResultHandler}
              />

              <MetadataSection
                selectedId={selectedId!}
                initialDisplayName={selectedAudit.displayName}
                initialDescription={selectedAudit.description ?? ""}
                onResult={makeResultHandler}
              />

              <LifecycleSection
                selectedId={selectedId!}
                onResult={makeResultHandler}
              />

              <DangerSection
                selectedId={selectedId!}
                onResult={makeResultHandler}
              />
            </div>

            {/* Sticky result panel — always visible, never scrolls away */}
            <div className="shrink-0 border-t border-border bg-background p-3">
              {result ? (
                <ResultPanel result={result} />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Run an action to see the result here.
                </p>
              )}
            </div>
          </div>

          {/* Right panel — live events feed */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <LiveEventsPanel auditId={selectedId!} />
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live Events Panel
// ---------------------------------------------------------------------------

function LiveEventsPanel({ auditId }: { auditId: string }) {
  const workspaceId = useWorkspaceId();
  const status = useQuery(api.audit_queries.getAuditSummaryStatus, {
    auditId: auditId as Id<"audits">,
    workspaceId,
  });

  const activities = useQuery(api.audit_queries.getAuditActivities, {
    auditId: auditId as Id<"audits">,
    workspaceId,
    limit: 30,
  });

  const isProcessing = status?.isGenerating ?? false;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Live Events
        </span>
        {isProcessing && (
          <span className="flex items-center gap-1.5 text-xs text-yellow-600">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-500" />
            Processing…
          </span>
        )}
        {!isProcessing && activities !== undefined && (
          <span className="text-xs text-muted-foreground">
            {activities.length} events
          </span>
        )}
      </div>

      {/* Status strip */}
      <div className="flex items-center gap-3 border-b border-border bg-muted/20 px-4 py-2">
        <span className="text-xs text-muted-foreground">Status:</span>
        {status ? (
          <AuditStatusBadge status={status.auditStatus} />
        ) : (
          <StatusBadge status={undefined} />
        )}
        <span className="text-xs text-muted-foreground">AI Summary:</span>
        <StatusBadge
          status={
            isProcessing
              ? "generating"
              : status?.hasSummary
                ? "completed"
                : "pending"
          }
        />
        {isProcessing && (
          <span className="text-xs text-yellow-600 animate-pulse">
            generating…
          </span>
        )}
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {activities === undefined && (
          <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
            Loading…
          </div>
        )}
        {activities?.length === 0 && (
          <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
            No events yet
          </div>
        )}
        {activities?.map((event) => (
          <AuditEventRow key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit event row
// ---------------------------------------------------------------------------

type AuditActivityType =
  | "created"
  | "status"
  | "summary"
  | "failure"
  | "checklist"
  | "lifecycle"
  | "update";

interface AuditActivity {
  id: string;
  type: AuditActivityType;
  description: string;
  timestamp: number;
  userName: string | null;
  metadata: unknown;
}

const AUDIT_TYPE_STYLES: Record<
  AuditActivityType,
  { pill: string; bar: string }
> = {
  failure: {
    pill: "bg-red-100 text-red-700 border-red-300",
    bar: "bg-red-400",
  },
  summary: {
    pill: "bg-green-100 text-green-700 border-green-300",
    bar: "bg-green-400",
  },
  created: {
    pill: "bg-blue-100 text-blue-700 border-blue-300",
    bar: "bg-blue-400",
  },
  lifecycle: {
    pill: "bg-purple-100 text-purple-700 border-purple-300",
    bar: "bg-purple-400",
  },
  status: {
    pill: "bg-amber-100 text-amber-700 border-amber-300",
    bar: "bg-amber-400",
  },
  checklist: {
    pill: "bg-cyan-100 text-cyan-700 border-cyan-300",
    bar: "bg-cyan-400",
  },
  update: {
    pill: "bg-zinc-100 text-zinc-600 border-zinc-300",
    bar: "bg-zinc-400",
  },
};

function AuditEventRow({ event }: { event: AuditActivity }) {
  const [expanded, setExpanded] = useState(false);
  const style = AUDIT_TYPE_STYLES[event.type] ?? AUDIT_TYPE_STYLES.update;
  const ago = formatAgo(event.timestamp);

  return (
    <div className="rounded border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start gap-2 px-2 py-1.5 text-left hover:bg-muted/30 transition-colors"
      >
        <span className={`mt-0.5 h-3 w-1 shrink-0 rounded-full ${style.bar}`} />
        <span
          className={`shrink-0 rounded border px-1 py-px text-[10px] font-semibold uppercase ${style.pill}`}
        >
          {event.type}
        </span>
        <span className="flex-1 text-xs leading-tight">
          {event.description}
        </span>
        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
          {ago}
        </span>
        {event.metadata != null && (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </button>

      {expanded && event.metadata != null && (
        <div className="border-t border-border bg-muted/10 px-2 py-1.5">
          <pre className="text-[10px] leading-relaxed overflow-auto max-h-40">
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status Override section
// ---------------------------------------------------------------------------

function StatusOverrideSection({
  selectedId,
  currentStatus,
  onResult,
}: {
  selectedId: string;
  currentStatus: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const updateAuditStatus = useMutation(api.audit_mutations.updateAuditStatus);

  return (
    <DevSection title="Status Machine Override">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Current status:</span>
        <AuditStatusBadge status={currentStatus} />
      </div>

      <div className="flex items-center gap-2">
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {AUDIT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <ActionButton
          label="Set Status →"
          onResult={onResult("Set Status")}
          onClick={async () =>
            updateAuditStatus({
              auditId: selectedId as Id<"audits">,
              status: selectedStatus as AuditStatus,
            })
          }
        />
      </div>

      {/* Transition reference table */}
      <div className="mt-1">
        <p className="mb-1.5 text-xs text-muted-foreground">
          Allowed transitions:
        </p>
        <table className="text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="pr-6 text-left font-medium">From</th>
              <th className="text-left font-medium">Allowed targets</th>
            </tr>
          </thead>
          <tbody>
            {TRANSITION_TABLE.map((row) => (
              <tr key={row.from}>
                <td className="pr-6 font-mono">{row.from}</td>
                <td className="font-mono text-muted-foreground">
                  {row.allowed.join(", ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// AI Actions section
// ---------------------------------------------------------------------------

function AIActionsSection({
  selectedId,
  aiSummaryData,
  onResult,
}: {
  selectedId: string;
  aiSummaryData: unknown;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const generateAuditSummary = useMutation(
    api.audit_mutations.generateAuditSummary,
  );

  return (
    <DevSection title="AI Actions">
      <ActionButton
        label="Generate Audit Summary"
        onResult={onResult("Generate Audit Summary")}
        onClick={async () =>
          generateAuditSummary({ auditId: selectedId as Id<"audits"> })
        }
      />
      <JsonViewer data={aiSummaryData ?? null} label="Existing AI Summary" />
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Metadata section
// ---------------------------------------------------------------------------

function MetadataSection({
  selectedId,
  initialDisplayName,
  initialDescription,
  onResult,
}: {
  selectedId: string;
  initialDisplayName: string;
  initialDescription: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [description, setDescription] = useState(initialDescription);
  const updateAudit = useMutation(api.audit_mutations.updateAudit);

  return (
    <DevSection title="Metadata">
      {/* Rename */}
      <div className="space-y-1">
        <label className="block text-xs text-muted-foreground">
          Display name
        </label>
        <div className="flex gap-2">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <ActionButton
            label="Update Name"
            onResult={onResult("Update Name")}
            onClick={async () =>
              updateAudit({
                auditId: selectedId as Id<"audits">,
                displayName,
              })
            }
          />
        </div>
      </div>

      {/* Description */}
      <div className="space-y-1">
        <label className="block text-xs text-muted-foreground">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <ActionButton
          label="Update Description"
          onResult={onResult("Update Description")}
          onClick={async () =>
            updateAudit({
              auditId: selectedId as Id<"audits">,
              description,
            })
          }
        />
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Lifecycle section
// ---------------------------------------------------------------------------

function LifecycleSection({
  selectedId,
  onResult,
}: {
  selectedId: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const [comments, setComments] = useState("");
  const [reason, setReason] = useState("");
  const archiveAudit = useMutation(api.audit_mutations.archiveAudit);
  const approveAudit = useMutation(api.audit_approve.approveAudit);
  const rejectAudit = useMutation(api.audit_reject.rejectAudit);

  return (
    <DevSection title="Lifecycle">
      <div className="space-y-2">
        <div className="space-y-1">
          <label className="block text-xs text-muted-foreground">
            Comments (for approve)
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={2}
            placeholder="Optional comments…"
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs text-muted-foreground">
            Reason (for reject)
          </label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Rejection reason…"
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <ActionButton
          label="Archive"
          onResult={onResult("Archive")}
          onClick={async () =>
            archiveAudit({ auditId: selectedId as Id<"audits"> })
          }
        />
        <ActionButton
          label="Approve"
          onResult={onResult("Approve")}
          onClick={async () =>
            approveAudit({
              auditId: selectedId as Id<"audits">,
              comments: comments || undefined,
            })
          }
        />
        <ActionButton
          label="Reject"
          onResult={onResult("Reject")}
          onClick={async () =>
            rejectAudit({
              auditId: selectedId as Id<"audits">,
              reason: reason || "No reason provided",
            })
          }
        />
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Danger Zone section
// ---------------------------------------------------------------------------

function DangerSection({
  selectedId,
  onResult,
}: {
  selectedId: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const [confirmId, setConfirmId] = useState("");
  const deleteAudit = useMutation(api.audit_mutations.deleteAudit);

  const shortId = selectedId.slice(-8);
  const confirmed = confirmId === shortId;

  return (
    <DevSection title="Danger Zone" danger>
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Type the last 8 chars of the audit ID to enable delete:
          <span className="ml-1 font-mono text-foreground">{shortId}</span>
        </p>
        <div className="flex gap-2">
          <input
            value={confirmId}
            onChange={(e) => setConfirmId(e.target.value)}
            placeholder={shortId}
            className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-red-500"
          />
          <ActionButton
            label="Delete"
            variant="destructive"
            disabled={!confirmed}
            onResult={(r) => {
              onResult("Delete Audit")(r);
              setConfirmId("");
            }}
            onClick={async () =>
              deleteAudit({ auditId: selectedId as Id<"audits"> })
            }
          />
        </div>
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/dev/audits")({
  component: AuditsPlaygroundPage,
});
